import { ServiceKindKey } from "./overviewModel";

/**
 * Which scope chips stay on the Topology bar once they no longer fit, and how
 * a viewer picks them (CTX-1488). Pure, so the rule is tested apart from the
 * measuring.
 */

/** A chip other than "All services": a service kind, or the CPEs. */
export type ScopeChipKey = ServiceKindKey | "cpe";

/** What a chip flags: how many need a look, red when any is down, else orange. */
export interface ChipIssues {
  count: number;
  isCritical: boolean;
}

export interface ScopeChipItem {
  key: ScopeChipKey;
  total: number;
  issues: ChipIssues;
}

/**
 * How many kinds sit on the bar before the viewer chooses, and how many a
 * tenant needs before there is anything to choose. Pins themselves have no cap:
 * past what fits, the bar scrolls (Daniel, 2026-10-27).
 */
export const PRINCIPAL_CHIPS = 4;

/** The order the bar has always used, and the order it shows. */
const CHIP_ORDER: ScopeChipKey[] = [
  "ports",
  "internet",
  "cloudConnectivity",
  "datacenterInterconnect",
  "fwaas",
  "sdwan",
  "cpe",
];

const byChipOrder = (a: ScopeChipItem, b: ScopeChipItem) => CHIP_ORDER.indexOf(a.key) - CHIP_ORDER.indexOf(b.key);

/** Until a viewer pins anything: the first four the tenant has, in the bar's order. */
export function defaultPinnedScopeChips(items: ScopeChipItem[], limit: number = PRINCIPAL_CHIPS): ScopeChipKey[] {
  return [...items]
    .sort(byChipOrder)
    .slice(0, limit)
    .map((item) => item.key);
}

/**
 * The chips on the bar (the pinned kinds this tenant has, in the bar's order)
 * and the rest. `pinned` null means the viewer never chose: the defaults hold.
 * An empty choice is a choice: the bar keeps "All services" alone.
 */
export function splitScopeChips(
  items: ScopeChipItem[],
  pinned: ScopeChipKey[] | null
): { principal: ScopeChipItem[]; more: ScopeChipItem[] } {
  const onBar = new Set(pinned ?? defaultPinnedScopeChips(items));
  const sorted = [...items].sort(byChipOrder);
  return {
    principal: sorted.filter((item) => onBar.has(item.key)),
    more: sorted.filter((item) => !onBar.has(item.key)),
  };
}

/** The pins after a click on `key`'s pin: a pinned kind comes off, an unpinned one goes on. */
export function togglePinnedScopeChip(pinned: ScopeChipKey[], key: ScopeChipKey): ScopeChipKey[] {
  return pinned.includes(key) ? pinned.filter((candidate) => candidate !== key) : [...pinned, key];
}

/** What a chip flags for several kinds: the trouble added up, red when any of it is down. */
export function sumIssues(items: ScopeChipItem[]): ChipIssues {
  return items.reduce<ChipIssues>(
    (acc, item) => ({
      count: acc.count + item.issues.count,
      isCritical: acc.isCritical || (item.issues.count > 0 && item.issues.isCritical),
    }),
    { count: 0, isCritical: false }
  );
}

const PINNED_KEY = "kontrolEdge.overview.pinnedScopeChips";

/**
 * The pins this browser saved, or null when it saved none (the defaults
 * hold). Storage that is blocked or holds something else reads as none.
 */
export function readPinnedScopeChips(): ScopeChipKey[] | null {
  try {
    const raw = window.localStorage.getItem(PINNED_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.filter((key): key is ScopeChipKey => CHIP_ORDER.includes(key as ScopeChipKey));
  } catch {
    return null;
  }
}

/** Saves the pins; a browser that refuses storage keeps them for this visit only. */
export function writePinnedScopeChips(keys: ScopeChipKey[]): void {
  try {
    window.localStorage.setItem(PINNED_KEY, JSON.stringify(keys));
  } catch {
    // Storage blocked (private mode, quota): the bar still works, unsaved.
  }
}
